import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  makeStyles,
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDialog-paper": {
      width: "400px",
      maxHeight: "none",
    },
  },
  textField: {
    margin: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(2),
    minHeight: "auto",
  },
}));

const ContactSourceModal = ({ open, onClose, contactSourceId, onSave }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [contactSource, setContactSource] = useState({
    name: "",
    description: "",
  });

  useEffect(() => {
    if (contactSourceId) {
      fetchContactSource();
    } else {
      setContactSource({
        name: "",
        description: "",
      });
    }
  }, [contactSourceId, open]);

  const fetchContactSource = async () => {
    if (!contactSourceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/contact-sources/${contactSourceId}`);
      setContactSource(data);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setContactSource({
      name: "",
      description: "",
    });
    onClose();
  };

  const handleSave = async () => {
    if (!contactSource.name.trim()) {
      toast.error(i18n.t("contactSources.validation.nameRequired"));
      return;
    }

    setLoading(true);
    try {
      if (contactSourceId) {
        await api.put(`/contact-sources/${contactSourceId}`, {
          name: contactSource.name,
          description: contactSource.description
        });
        toast.success(i18n.t("contactSources.toasts.updated"));
      } else {
        await api.post("/contact-sources", {
          name: contactSource.name,
          description: contactSource.description
        });
        toast.success(i18n.t("contactSources.toasts.created"));
      }
      onSave();
      handleClose();
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactSource((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth className={classes.root}>
      <DialogTitle>
        {contactSourceId
          ? i18n.t("contactSources.modal.editTitle")
          : i18n.t("contactSources.modal.addTitle")}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <TextField
          autoFocus
          margin="dense"
          name="name"
          label={i18n.t("contactSources.form.name")}
          type="text"
          fullWidth
          variant="outlined"
          value={contactSource.name}
          onChange={handleInputChange}
          className={classes.textField}
        />
        <TextField
          margin="dense"
          name="description"
          label={i18n.t("contactSources.form.description")}
          type="text"
          fullWidth
          variant="outlined"
          multiline
          rows={2}
          value={contactSource.description}
          onChange={handleInputChange}
          className={classes.textField}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={loading}>
          {i18n.t("contactSources.buttons.cancel")}
        </Button>
        <Button onClick={handleSave} color="primary" disabled={loading}>
          {loading
            ? i18n.t("contactSources.buttons.saving")
            : i18n.t("contactSources.buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactSourceModal; 